/**
 * Shared opening-sketch placement machinery.
 *
 * OPENING_SKETCH annotations are parametric on a wall sketch edge
 * (edge id + along-wall offset + length + vertical bounds). Multiple tools
 * place and manipulate them — the Layout persona's OpeningSketchDrawTool and
 * the floorplan design tab's tools (SketchObjectTool door/window items, the
 * Magic door/window commit) — so the geometry helpers, request builders,
 * undo snapshots and overlay visuals live here rather than in any one tool.
 */

import * as THREE from 'three';
import { v4 as uuid } from 'uuid';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { OpeningKind, DoorType, STANDARD_WINDOW_TYPE, type DoorGlyphType, type WindowGlyphType } from 'shared-types';
import { Viewer } from '../../../../viewer/viewer';
import { PointoramaWallSketch } from '../../../../utils/PointoramaWallSketch';
import { PixelHitTester } from '../selection/PixelHitTester';
import { Snapshot } from '../../ToolUndoRedoManager';
import { projectMouseToFloor } from '../../implementations/WallSketchSnapEngine';
import { pointAtArcLength, edgeLength as arcEdgeLength, closestPointOnEdge, subSpan, tessellateEdge } from '../../../../utils/skeleton/arcGeometry';
import type { OpeningSketchRequest } from '../../../../utils/skeleton/replicateOpeningSketches';
import { buildOpeningGlyph, customGlyphTypeOf } from '../../../../utils/skeleton/openingGlyphs';
import {
  getOpeningSketchWallEdgeIdProperty,
  getOpeningSketchOffsetProperty,
  getOpeningSketchLengthProperty,
  getOpeningSketchLowerBoundProperty as getInstanceLowerBoundProperty,
  getOpeningSketchHeightProperty,
  getOpeningSketchInsetProperty,
  getOpeningSketchKindProperty,
  getOpeningSketchDoorTypeProperty,
  getOpeningSketchWindowTypeProperty,
  getOpeningSketchHingeAtStartProperty,
  getOpeningSketchSwingPositiveProperty,
  getOpeningSketchRollableProperty,
  getOpeningSketchSourceOpeningIdProperty,
} from '@pointorama/potree-shared';
import type { ReactiveProperty } from '@pointorama/potree-shared';
import {
  getOpeningSketchFixedLengthProperty,
  getOpeningSketchDefaultLengthProperty,
  getOpeningSketchLowerBoundProperty as getUserLowerBoundProperty,
  getOpeningSketchUpperBoundProperty as getUserUpperBoundProperty,
} from '../../../../../modules/Properties/integration/UserSettingsIntegration';

// ─── Pick / snap radii (px) shared by every opening-placing tool ───────
export const OPENING_EDGE_RADIUS_PX = 14;
export const OPENING_NODE_SNAP_RADIUS_PX = 16;
export const OPENING_GLYPH_PICK_RADIUS_PX = 10;
/** Radius for picking an opening by its footprint LINE on the wall (as opposed
 *  to its glyph symbol) — the whole span is grabbable, not just the symbol. */
export const OPENING_FOOTPRINT_PICK_RADIUS_PX = 12;

export const MIN_OPENING_LENGTH = 0.05;

// Corner-clear span helpers — every opening-placing tool imports them from
// here; the implementation lives with the skeleton math so it stays
// unit-testable without the viewer module graph.
export { openingClearSpanOnEdge, clampOpeningOffsetToClearSpan } from '../../../../utils/skeleton/openingClearSpan';

/** A point snapped onto a wall edge: which edge + along-wall offset from its start node. */
export interface OpeningEdgeHit {
  edgeId: string;
  offset: number;
  /** World position of the snapped point (on the wall centerline). */
  point: THREE.Vector3;
}

// ─── Edge geometry ─────────────────────────────────────────────────────

/** World point at an along-edge (arc-length) offset on a wall edge. */
export function openingEdgeWorldPoint(skeleton: PointoramaWallSketch, edgeId: string, offset: number): THREE.Vector3 | null {
  const edge = skeleton.getEdge(edgeId);
  if (!edge) return null;
  const a = skeleton.getNode(edge.startNodeId);
  const b = skeleton.getNode(edge.endNodeId);
  if (!a || !b) return null;
  const aXY = { x: a.position.x, y: a.position.y };
  const bXY = { x: b.position.x, y: b.position.y };
  if (Math.hypot(bXY.x - aXY.x, bXY.y - aXY.y) < 1e-6) return null;
  const { point } = pointAtArcLength(aXY, bXY, edge.bulge, offset);
  return new THREE.Vector3(point.x, point.y, skeleton.getFloorHeight());
}

/** Arc length of a wall edge (0 when unresolvable). */
export function openingEdgeLength(skeleton: PointoramaWallSketch, edgeId: string): number {
  const edge = skeleton.getEdge(edgeId);
  if (!edge) return 0;
  const a = skeleton.getNode(edge.startNodeId);
  const b = skeleton.getNode(edge.endNodeId);
  if (!a || !b) return 0;
  return arcEdgeLength({ x: a.position.x, y: a.position.y }, { x: b.position.x, y: b.position.y }, edge.bulge);
}

/**
 * World polyline of the span between two along-edge offsets — a plain 2-point
 * segment on straight walls, a tessellated arc on curved ones.
 */
export function openingSpanPolyline(skeleton: PointoramaWallSketch, edgeId: string, s0: number, s1: number): THREE.Vector3[] | null {
  const edge = skeleton.getEdge(edgeId);
  if (!edge) return null;
  const a = skeleton.getNode(edge.startNodeId);
  const b = skeleton.getNode(edge.endNodeId);
  if (!a || !b) return null;
  const span = subSpan({ x: a.position.x, y: a.position.y }, { x: b.position.x, y: b.position.y }, edge.bulge, s0, s1);
  const z = skeleton.getFloorHeight();
  return tessellateEdge(span.start, span.end, span.bulge).map((p) => new THREE.Vector3(p.x, p.y, z));
}

/** The world endpoints (+ full polyline) of an existing opening's footprint on
 *  its wall, from its persisted edge / offset / length. Null if any lookup fails. */
export function openingFootprint(skeleton: PointoramaWallSketch, openingId: string): { a: THREE.Vector3; b: THREE.Vector3; pts: THREE.Vector3[] } | null {
  let edgeId = '';
  let offset = 0;
  let length = 0;
  try {
    edgeId = getOpeningSketchWallEdgeIdProperty(openingId).value || '';
    offset = getOpeningSketchOffsetProperty(openingId).value ?? 0;
    length = getOpeningSketchLengthProperty(openingId).value ?? 0;
  } catch {
    return null;
  }
  if (!edgeId) return null;
  const pts = openingSpanPolyline(skeleton, edgeId, offset, offset + length);
  if (!pts || pts.length < 2) return null;
  return { a: pts[0], b: pts[pts.length - 1], pts };
}

/**
 * Pick the opening under the cursor: door/window glyph symbol first, then the
 * opening's footprint LINE along the wall. Returns the opening id, or null.
 */
export function pickOpeningSketchAtPixel(viewer: Viewer, skeleton: PointoramaWallSketch, mousePx: THREE.Vector2, hitTester: PixelHitTester): string | null {
  const glyphPick = skeleton.pickOpeningGlyphAtPixel(mousePx, hitTester, OPENING_GLYPH_PICK_RADIUS_PX);
  if (glyphPick) return glyphPick.openingId;

  let bestId: string | null = null;
  let bestDist = OPENING_FOOTPRINT_PICK_RADIUS_PX;
  for (const opening of viewer.scene.openingSketches) {
    if (opening.groupIdentifier !== skeleton.groupIdentifier) continue;
    const footprint = openingFootprint(skeleton, opening.identifier);
    if (!footprint) continue;
    let prev: THREE.Vector2 | null = null;
    for (const p of footprint.pts) {
      const px = hitTester.projectWorldToPixel(p);
      if (!px) {
        prev = null;
        continue;
      }
      if (prev) {
        const d = PixelHitTester.pointToSegmentDistancePx(mousePx, prev, px);
        if (d < bestDist) {
          bestDist = d;
          bestId = opening.identifier;
        }
      }
      prev = px;
    }
  }
  return bestId;
}

/** Cursor's along-wall offset on a specific edge (no node snapping — for dragging). */
export function cursorOffsetOnOpeningEdge(event: MouseEvent, viewer: Viewer, skeleton: PointoramaWallSketch, edgeId: string): number | null {
  const edge = skeleton.getEdge(edgeId);
  if (!edge) return null;
  const a = skeleton.getNode(edge.startNodeId);
  const b = skeleton.getNode(edge.endNodeId);
  if (!a || !b) return null;
  const camera = viewer.scene.getActiveCamera();
  const floor = projectMouseToFloor(event, viewer.renderer.domElement, camera, skeleton.getFloorHeight());
  if (!floor) return null;
  const aXY = { x: a.position.x, y: a.position.y };
  const bXY = { x: b.position.x, y: b.position.y };
  if (Math.hypot(bXY.x - aXY.x, bXY.y - aXY.y) < 1e-6) return null;
  const len = arcEdgeLength(aXY, bXY, edge.bulge);
  const foot = closestPointOnEdge({ x: floor.x, y: floor.y }, aXY, bXY, edge.bulge);
  return Math.max(0, Math.min(len, foot.s));
}

/** Cursor's SIGNED perpendicular distance off the edge centerline (positive
 *  on the +90°-CCW side of the local tangent — the `swing_positive` side).
 *  Judged at the cursor's foot point, so it follows curved walls. */
export function cursorPerpOnOpeningEdge(event: MouseEvent, viewer: Viewer, skeleton: PointoramaWallSketch, edgeId: string): number | null {
  const edge = skeleton.getEdge(edgeId);
  if (!edge) return null;
  const a = skeleton.getNode(edge.startNodeId);
  const b = skeleton.getNode(edge.endNodeId);
  if (!a || !b) return null;
  const floor = projectMouseToFloor(event, viewer.renderer.domElement, viewer.scene.getActiveCamera(), skeleton.getFloorHeight());
  if (!floor) return null;
  const aXY = { x: a.position.x, y: a.position.y };
  const bXY = { x: b.position.x, y: b.position.y };
  if (Math.hypot(bXY.x - aXY.x, bXY.y - aXY.y) < 1e-6) return null;
  const foot = closestPointOnEdge({ x: floor.x, y: floor.y }, aXY, bXY, edge.bulge);
  const { point, tangent } = pointAtArcLength(aXY, bXY, edge.bulge, foot.s);
  return tangent.x * (floor.y - point.y) - tangent.y * (floor.x - point.x);
}

/** Undo snapshot for a float property change: toggles between before/after. */
export function createOpeningFloatToggleSnapshot(getter: (id: string) => ReactiveProperty<number>, openingId: string, before: number, after: number): Snapshot {
  let applyBefore = true;
  const snapshot: Snapshot = {
    restore: (): Snapshot => {
      try {
        getter(openingId).value = applyBefore ? before : after;
      } catch {
        /* property service not ready */
      }
      applyBefore = !applyBefore;
      return snapshot;
    },
  };
  return snapshot;
}

// ─── Per-user, per-kind settings ───────────────────────────────────────

export interface OpeningPlacementSettings {
  kind: OpeningKind;
  /** When true, placement uses `length`; when false, length is free-drawn. */
  fixedLength: boolean;
  length: number;
  /** Sill / head heights (m above floor) — always applied. */
  lowerBound: number;
  upperBound: number;
}

/**
 * Read `kind`'s persisted per-user placement settings. Each property owns its
 * kind-appropriate default; when the property service isn't ready the
 * `previous` values are kept (with the kind adopted).
 */
export function loadOpeningSettingsForKind(kind: OpeningKind, previous?: Omit<OpeningPlacementSettings, 'kind'>): OpeningPlacementSettings {
  const fallback = previous ?? { fixedLength: false, length: 0.9, lowerBound: 0, upperBound: 2.1 };
  try {
    const fl = getOpeningSketchFixedLengthProperty(kind).value;
    const len = getOpeningSketchDefaultLengthProperty(kind).value;
    const lo = getUserLowerBoundProperty(kind).value;
    const hi = getUserUpperBoundProperty(kind).value;
    return {
      kind,
      fixedLength: typeof fl === 'boolean' ? fl : fallback.fixedLength,
      length: typeof len === 'number' ? len : fallback.length,
      lowerBound: typeof lo === 'number' ? lo : fallback.lowerBound,
      upperBound: typeof hi === 'number' ? hi : fallback.upperBound,
    };
  } catch {
    return { kind, ...fallback };
  }
}

// ─── Requests ──────────────────────────────────────────────────────────

/**
 * Default `inset` for a freshly-generated opening: HALF the wall thickness,
 * so the door/window sits on the wall's mid-thickness plane instead of flush
 * with the outer face (`signedPerpFromInset`: perp = leftSize − inset =
 * (leftSize − rightSize)/2 → the centerline on symmetric walls).
 */
export function defaultOpeningInset(skeleton: PointoramaWallSketch, edgeId: string): number {
  const edge = skeleton.getEdge(edgeId);
  return ((edge?.leftSize ?? 0.1) + (edge?.rightSize ?? 0.1)) / 2;
}

/**
 * Build a `request_opening_sketch` payload for a fresh opening on a wall
 * edge. Bounds are floor-relative sill/head heights; the request stores an
 * absolute lower Z + relative height. The identifier is minted here so undo
 * can re-create the identical opening. Null when the sketch has no group.
 */
export function buildOpeningSketchRequest(
  skeleton: PointoramaWallSketch,
  opts: { edgeId: string; offset: number; length: number; kind: OpeningKind; lowerBound: number; upperBound: number; doorType?: DoorGlyphType; windowType?: WindowGlyphType },
): OpeningSketchRequest | null {
  const groupIdentifier = skeleton.groupIdentifier;
  if (!groupIdentifier) return null;
  const floorZ = skeleton.getFloorHeight();
  return {
    identifier: `opening_sketch_${groupIdentifier}_${uuid()}`,
    groupIdentifier,
    wallEdgeId: opts.edgeId,
    offset: opts.offset,
    length: opts.length,
    lowerBound: floorZ + opts.lowerBound,
    height: Math.max(0, opts.upperBound - opts.lowerBound),
    inset: defaultOpeningInset(skeleton, opts.edgeId), // centered in the wall
    kind: opts.kind,
    doorType: opts.doorType ?? DoorType.SINGLE,
    windowType: opts.windowType ?? STANDARD_WINDOW_TYPE,
    hingeAtStart: true,
    swingPositive: true,
    rollable: false,
    sourceOpeningId: '', // hand-drawn (not replicated from a floorplan opening)
  };
}

/**
 * Capture an EXISTING opening sketch's full parameter set as a request that
 * would re-create it identically — the delete-undo counterpart of
 * {@link buildOpeningSketchRequest}. Null when the property service is not
 * ready.
 */
export function captureOpeningSketchRequest(openingId: string, groupIdentifier: string): OpeningSketchRequest | null {
  try {
    return {
      identifier: openingId,
      groupIdentifier,
      wallEdgeId: getOpeningSketchWallEdgeIdProperty(openingId).value || '',
      offset: getOpeningSketchOffsetProperty(openingId).value ?? 0,
      length: getOpeningSketchLengthProperty(openingId).value ?? 0,
      lowerBound: getInstanceLowerBoundProperty(openingId).value ?? 0,
      height: getOpeningSketchHeightProperty(openingId).value ?? 0,
      inset: getOpeningSketchInsetProperty(openingId).value ?? 0,
      kind: getOpeningSketchKindProperty(openingId).value,
      doorType: getOpeningSketchDoorTypeProperty(openingId).value,
      windowType: getOpeningSketchWindowTypeProperty(openingId).value,
      hingeAtStart: getOpeningSketchHingeAtStartProperty(openingId).value,
      swingPositive: getOpeningSketchSwingPositiveProperty(openingId).value,
      rollable: getOpeningSketchRollableProperty(openingId).value,
      sourceOpeningId: getOpeningSketchSourceOpeningIdProperty(openingId).value || '',
    };
  } catch {
    return null;
  }
}

/** The geometric parameter subset of an opening sketch (elevation editing). */
export interface OpeningSketchGeometryValues {
  wallEdgeId: string;
  offset: number;
  length: number;
  /** Absolute world Z of the sill. */
  lowerBound: number;
  height: number;
}

/** Read an opening sketch's geometric parameters, or null when unavailable. */
export function readOpeningSketchGeometry(openingId: string): OpeningSketchGeometryValues | null {
  try {
    return {
      wallEdgeId: getOpeningSketchWallEdgeIdProperty(openingId).value || '',
      offset: getOpeningSketchOffsetProperty(openingId).value ?? 0,
      length: getOpeningSketchLengthProperty(openingId).value ?? 0,
      lowerBound: getInstanceLowerBoundProperty(openingId).value ?? 0,
      height: getOpeningSketchHeightProperty(openingId).value ?? 0,
    };
  } catch {
    return null;
  }
}

/** Write an opening sketch's geometric parameters (no-op when the property
 *  service isn't ready). */
export function writeOpeningSketchGeometry(openingId: string, values: OpeningSketchGeometryValues): void {
  try {
    getOpeningSketchWallEdgeIdProperty(openingId).value = values.wallEdgeId;
    getOpeningSketchOffsetProperty(openingId).value = values.offset;
    getOpeningSketchLengthProperty(openingId).value = values.length;
    getInstanceLowerBoundProperty(openingId).value = values.lowerBound;
    getOpeningSketchHeightProperty(openingId).value = values.height;
  } catch {
    /* property service not ready */
  }
}

// ─── Undo snapshots ────────────────────────────────────────────────────

/**
 * Existence-toggle snapshot for an opening sketch. When `existsInitially`,
 * the first restore() (undo) deletes it and the next re-creates it with the
 * same identifier + params; when created from a delete, the polarity flips.
 */
export function createOpeningToggleSnapshot(viewer: Viewer, request: OpeningSketchRequest, existsInitially: boolean): Snapshot {
  let exists = existsInitially;
  const snapshot: Snapshot = {
    restore: (): Snapshot => {
      if (exists) {
        const obj = viewer.scene.openingSketches.find((o) => o.identifier === request.identifier);
        if (obj) viewer.scene.dispatchEvent({ type: 'measurement_delete', value: { measure: obj } });
      } else {
        viewer.scene.dispatchEvent({ type: 'request_opening_sketch', value: request });
      }
      exists = !exists;
      return snapshot;
    },
  };
  return snapshot;
}

/** Undo snapshot for a full geometry edit (elevation rectangle move/resize):
 *  toggles the parameter set between before/after and re-cuts the wall hole. */
export function createOpeningGeometryEditSnapshot(getWallSketch: () => PointoramaWallSketch | null, openingId: string, before: OpeningSketchGeometryValues, after: OpeningSketchGeometryValues): Snapshot {
  let applyBefore = true;
  const snapshot: Snapshot = {
    restore: (): Snapshot => {
      writeOpeningSketchGeometry(openingId, applyBefore ? before : after);
      getWallSketch()?.updateSkinningVisualization();
      applyBefore = !applyBefore;
      return snapshot;
    },
  };
  return snapshot;
}

/** Undo snapshot for an offset move: toggles between before/after and re-cuts
 *  the wall hole (the offset write alone only patches the glyph). */
export function createOpeningOffsetMoveSnapshot(getWallSketch: () => PointoramaWallSketch | null, openingId: string, before: number, after: number): Snapshot {
  let applyBefore = true;
  const snapshot: Snapshot = {
    restore: (): Snapshot => {
      try {
        getOpeningSketchOffsetProperty(openingId).value = applyBefore ? before : after;
        getWallSketch()?.updateSkinningVisualization();
      } catch {
        /* property service not ready */
      }
      applyBefore = !applyBefore;
      return snapshot;
    },
  };
  return snapshot;
}

// ─── Wall-hole rebuild coalescing ──────────────────────────────────────

/**
 * Re-cut the wall hole live while sliding an opening, throttled to one full
 * skin rebuild per animation frame (updateSkinningVisualization is a
 * synchronous main-thread Manifold CSG pass). Cancel on drag-end / disable.
 */
export class WallHoleRebuildScheduler {
  private raf: number | null = null;
  constructor(private readonly getWallSketch: () => PointoramaWallSketch | null) {}

  schedule(): void {
    if (this.raf !== null) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = null;
      this.getWallSketch()?.updateSkinningVisualization();
    });
  }

  cancel(): void {
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
  }
}

// ─── Footprint highlight overlay ───────────────────────────────────────

/** Brand selection color — highlight over hovered/selected opening footprints. */
const HIGHLIGHT_COLOR = new THREE.Color('#269FC6');
const HIGHLIGHT_WIDTH = 8;
const HIGHLIGHT_RENDER_ORDER = 999;

/**
 * Selection/slide highlight over one or more openings' footprint polylines —
 * each polyline decomposes into segment pairs so a single LineSegments2
 * renders the whole (multi-)selection. The host tool supplies the overlay
 * group (lazily) so the highlight lives in that tool's overlay lifecycle.
 */
export class OpeningFootprintHighlight {
  private line: LineSegments2 | null = null;
  private geometry: LineSegmentsGeometry | null = null;
  private material: LineMaterial | null = null;

  constructor(
    private readonly viewer: Viewer,
    private readonly getGroup: () => THREE.Group,
  ) {}

  show(polylines: THREE.Vector3[][]): void {
    const positions: number[] = [];
    for (const pts of polylines) {
      for (let i = 0; i + 1 < pts.length; i++) {
        positions.push(pts[i].x, pts[i].y, pts[i].z + 0.05, pts[i + 1].x, pts[i + 1].y, pts[i + 1].z + 0.05);
      }
    }
    if (positions.length === 0) {
      this.hide();
      return;
    }
    const resolution = new THREE.Vector2(this.viewer.renderer.domElement.clientWidth || 1000, this.viewer.renderer.domElement.clientHeight || 1000);
    if (!this.line) {
      this.geometry = new LineSegmentsGeometry();
      this.geometry.setPositions(positions);
      this.material = new LineMaterial({
        color: HIGHLIGHT_COLOR.getHex(),
        linewidth: HIGHLIGHT_WIDTH,
        resolution,
        depthTest: false,
        transparent: true,
      });
      this.line = new LineSegments2(this.geometry, this.material);
      this.line.renderOrder = HIGHLIGHT_RENDER_ORDER;
      this.line.computeLineDistances();
      this.getGroup().add(this.line);
    } else {
      // Swap the geometry — LineSegmentsGeometry buffers are fixed at construction.
      const old = this.geometry;
      this.geometry = new LineSegmentsGeometry();
      this.geometry.setPositions(positions);
      this.line.geometry = this.geometry;
      old?.dispose();
      this.line.computeLineDistances();
      this.material?.resolution.copy(resolution);
    }
    this.line.visible = true;
  }

  hide(): void {
    if (this.line) this.line.visible = false;
  }

  dispose(): void {
    if (this.line) {
      this.line.parent?.remove(this.line);
      this.geometry?.dispose();
      this.material?.dispose();
      this.line = null;
      this.geometry = null;
      this.material = null;
    }
  }
}

// ─── Library thumbnails ────────────────────────────────────────────────

/**
 * Flat `[x,y,z,…]` glyph segments for a door/window library card — the same
 * floor-plan symbol the wall sketch renders, drawn in a unit frame at the
 * origin at the kind's default width. Built-in symbols are cached (pure
 * geometry); organisation ones follow their (republishable) definition.
 */
const thumbnailCache = new Map<string, number[]>();
export function buildOpeningLibraryThumbnail(kind: OpeningKind, glyph: { doorType?: DoorGlyphType; windowType?: WindowGlyphType } = {}): number[] {
  const doorType = glyph.doorType ?? DoorType.SINGLE;
  const windowType = glyph.windowType ?? STANDARD_WINDOW_TYPE;
  const input = {
    frame: { center: { x: 0, y: 0 }, wallDir: { x: 1, y: 0 }, wallNorm: { x: 0, y: 1 }, halfWidth: kind === OpeningKind.WINDOW ? 0.6 : 0.45 },
    kind,
    wallThickness: 0.2,
    hingeAtStart: true,
    swingPositive: true,
    doorType,
    windowType,
    rollable: false,
  };
  if (customGlyphTypeOf(input)) return buildOpeningGlyph(input, 0).segments;
  const key = `${kind}|${doorType}|${windowType}`;
  const cached = thumbnailCache.get(key);
  if (cached) return cached;
  const { segments } = buildOpeningGlyph(input, 0);
  thumbnailCache.set(key, segments);
  return segments;
}
